import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FuseNavigationService } from 'src/@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from 'src/@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from 'src/@fuse/services/config.service';
import { FuseSplashScreenService } from 'src/@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from 'src/@fuse/services/translation-loader.service';
import { UsersConfiguracionesService } from 'src/app/main/pages/services/user-configuraciones.service';
import { locale as navigationEnglish } from 'src/app/navigation/i18n/en';
import { locale as navigationTurkish } from 'src/app/navigation/i18n/tr';
import { navigation } from 'src/app/navigation/navigation';
import { navigationBeforeReg } from 'src/app/navigation/navigation-before-reg';
import {
  AppState,
  currentEstablecimientoSelected,
  currentUser,
  isEstablecimientoSelected,
  isUserLoaded,
  UserRequested,
} from 'src/app/store';

import { SeleccionarTipoUsoComponent } from './main/pages/generals/seleccionar-tipo-uso/seleccionar-tipo-uso.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  fuseConfig: any;
  navigation: any;
  navigationBeforeReg: any;
  hiddenSettings = true;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _fuseConfigService: FuseConfigService,
    private _fuseNavigationService: FuseNavigationService,
    private _fuseSidebarService: FuseSidebarService,
    private _fuseSplashScreenService: FuseSplashScreenService,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private _translateService: TranslateService,
    private _platform: Platform,
    private _store: Store<AppState>,
    public _dialog: MatDialog,
    private _userConfiguracionesService: UsersConfiguracionesService,
  ) {
    // Get default navigation
    this.navigation = navigation;
    this.navigationBeforeReg = navigationBeforeReg;

    // Register the navigation to the service
    this._fuseNavigationService.register('main', this.navigation);
    this._fuseNavigationService.register(
      'before-reg',
      this.navigationBeforeReg,
    );

    // Set the main navigation as our current navigation
    this._fuseNavigationService.setCurrentNavigation('main');
    this._fuseNavigationService.setCurrentNavigation('before-reg');

    // Add languages
    this._translateService.addLangs(['en', 'tr']);

    // Set the default language
    this._translateService.setDefaultLang('en');

    // Set the navigation translations
    this._fuseTranslationLoaderService.loadTranslations(
      navigationEnglish,
      navigationTurkish,
    );

    // Use a language
    this._translateService.use('en');

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     **/

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.

    setTimeout(() => {
      this._translateService.setDefaultLang('en');
      this._translateService.setDefaultLang('tr');
    });

    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    //this.limitedloginService.getDefaultToken();
  }

  // eslint-disable-next-line
  ngOnInit(): void {
    // Subscribe to config changes
    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: any) => {
        this.fuseConfig = config;

        // Boxed
        if (this.fuseConfig.layout.width === 'boxed') {
          this.document.body.classList.add('boxed');
        } else {
          this.document.body.classList.remove('boxed');
        }

        // Color theme - Use normal for loop for IE11 compatibility
        for (let i = 0; i < this.document.body.classList.length; i++) {
          const className = this.document.body.classList[i];

          if (className.startsWith('theme-')) {
            this.document.body.classList.remove(className);
          }
        }

        this.document.body.classList.add(this.fuseConfig.colorTheme);
      });

    this._store
      .pipe(
        select(currentUser),
        takeUntil(this._unsubscribeAll),
        filter(user => user),
      )
      .subscribe(user => {
        if (
          user.userConfiguraciones &&
          user.userConfiguraciones.mensajeInicial
        ) {
          const dialogRef = this._dialog.open(SeleccionarTipoUsoComponent);

          dialogRef.afterClosed().subscribe(data => {
            this._userConfiguracionesService.updateMensajeInicial().subscribe(
              response => {
                this._store.dispatch(new UserRequested());
              },
              err => {
                console.error(err);
              },
            );
          });
        }
      });

    this._store
      .pipe(
        select(isEstablecimientoSelected),
        takeUntil(this._unsubscribeAll),
        filter(isSelected => isSelected),
      )
      .subscribe(isSelected => {
        this._store
          .pipe(
            select(currentEstablecimientoSelected),
            takeUntil(this._unsubscribeAll),
            filter(establecimiento => establecimiento),
          )
          .subscribe(establecimiento => {
            const childrens: any = [];
            establecimiento.bodegas.forEach((bodega: { tipo: string }) => {
              const value = bodega.tipo.replace('_', '-').toLowerCase();
              childrens.push({
                id: bodega.tipo,
                title: bodega.tipo,
                type: 'item',
                icon: 'library_books',
                url: '/inventario/' + value + '/existencias',
              });
            });

            if (isSelected) {
              this._fuseNavigationService.setCurrentNavigation('main');

              const item =
                this._fuseNavigationService.getNavigationItem('inventario');
              item.children = childrens;

              this._fuseNavigationService.updateNavigationItem(
                'inventario',
                item,
              );
            } else {
              this._fuseNavigationService.setCurrentNavigation('before-reg');
            }
          });

        if (!isSelected) {
          this._fuseNavigationService.setCurrentNavigation('before-reg');
        }
      });

    this._store
      .pipe(
        select(isUserLoaded),
        takeUntil(this._unsubscribeAll),
        filter(isUserLoaded => !isUserLoaded),
      )
      .subscribe(isUserLoaded => {
        this._fuseNavigationService.setCurrentNavigation('before-reg');
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }

  toggleSidebarOpen(key: any): void {
    this._fuseSidebarService.getSidebar(key)?.toggleOpen();
  }
}
